import { useState, useEffect } from "react";
import { adminApi, AdminUser, ApiError } from "../lib/api";
import { Send, Loader2, CheckCircle, AlertCircle, Search } from "lucide-react";
import { cn } from "../lib/utils";

export default function Notifications() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");

  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    adminApi
      .listUsers()
      .then((res) => setUsers(res.data))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, []);

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSend = async () => {
    if (!selectedUser || !title.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);

    try {
      await adminApi.sendNotification({
        user_id: selectedUser.id,
        title: title.trim(),
        body: body.trim(),
      });
      setResult({
        type: "success",
        message: `Notification sent to ${selectedUser.name}`,
      });
      setTitle("");
      setBody("");
    } catch (err) {
      setResult({
        type: "error",
        message:
          err instanceof ApiError ? err.message : "Failed to send notification",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">
        Send Notification
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Selection */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">
            Select User
          </h2>

          <div className="relative mb-4">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition-all"
            />
          </div>

          {loadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className={cn(
                    "w-full text-left px-4 py-3 rounded-xl flex items-center justify-between transition-colors",
                    selectedUser?.id === user.id
                      ? "bg-slate-900 text-white"
                      : "hover:bg-slate-50 text-slate-900",
                  )}
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {user.name}
                    </p>
                    <p
                      className={cn(
                        "text-xs truncate",
                        selectedUser?.id === user.id
                          ? "text-slate-300"
                          : "text-slate-500",
                      )}
                    >
                      {user.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {user.pushToken ? (
                      <span
                        className={cn(
                          "text-[10px] font-bold px-2 py-0.5 rounded-full",
                          selectedUser?.id === user.id
                            ? "bg-emerald-400/20 text-emerald-300"
                            : "bg-emerald-50 text-emerald-600",
                        )}
                      >
                        PUSH
                      </span>
                    ) : (
                      <span
                        className={cn(
                          "text-[10px] font-bold px-2 py-0.5 rounded-full",
                          selectedUser?.id === user.id
                            ? "bg-slate-700 text-slate-400"
                            : "bg-slate-100 text-slate-400",
                        )}
                      >
                        NO PUSH
                      </span>
                    )}
                  </div>
                </button>
              ))}
              {filteredUsers.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">
                  No users found
                </p>
              )}
            </div>
          )}
        </div>

        {/* Notification Form */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">
            Compose Notification
          </h2>

          {selectedUser ? (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="size-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
                  {selectedUser.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold">{selectedUser.name}</p>
                  <p className="text-xs text-slate-500">{selectedUser.email}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Notification title..."
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Body
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Notification body..."
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition-all resize-none"
                />
              </div>

              {result && (
                <div
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium",
                    result.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                      : "bg-red-50 text-red-700 border border-red-100",
                  )}
                >
                  {result.type === "success" ? (
                    <CheckCircle size={16} />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  {result.message}
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={sending || !title.trim() || !body.trim()}
                className="w-full bg-slate-900 text-white font-semibold py-2.5 rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {sending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {sending ? "Sending..." : "Send Notification"}
              </button>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Send size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a user to compose a notification</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
